/**
 * JobMaster API client for the web admin.
 * Talks to the same Hono backend as the mobile app.
 * Base URL comes from NEXT_PUBLIC_BACKEND_URL env var.
 *
 * Features:
 * - Auto-attaches JWT Bearer access token, held ONLY in memory (see below)
 * - Global 401 interceptor → clears session, redirects to /login
 * - Silent error reporting to Corp DB for 500+ and 401 errors
 */

import { reportApiError } from './error-reporter'
import { getAccessToken, setAccessToken } from './auth-token'

export { getAccessToken, setAccessToken }

export let BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3000'

if (typeof window !== 'undefined' && !process.env.NEXT_PUBLIC_BACKEND_URL) {
  const hostname = window.location.hostname
  const protocol = window.location.protocol
  const port = protocol === 'https:' ? '3443' : '3000'

  BACKEND_URL = `${protocol}//${hostname}:${port}`
}

// ─────────────────────────────────────────────────────────────────────────
// QA WEB-003 (Tony, 2026-09-18) — access token lives ONLY in memory.
//
// The refresh token is never sent to the browser at all for web clients —
// the backend sets it as an httpOnly `jm_refresh` cookie (Secure, SameSite=
// None, Path=/api/mvp-auth) instead. The access token itself (getAccessToken
// / setAccessToken, re-exported above from ./auth-token) is a plain
// module-level variable: it is NOT written to localStorage or sessionStorage
// anywhere. That means every browser window — the main tab, a full reload,
// and every popout (a popout is a genuinely separate JS realm — it does NOT
// share this module's memory even though it shares localStorage) — boots
// with an empty token and must re-mint one from the httpOnly cookie via
// `refreshAccessToken()` before its first authenticated call. `EXPIRES_KEY`
// in localStorage is NOT secret — it only carries the access token's expiry
// timestamp so AuthContext can schedule proactive refreshes; it cannot be
// used to authenticate anything.
// See docs/BACKEND_ARCHITECTURE.md "Session & Auth" and web/CLAUDE.md.
// ─────────────────────────────────────────────────────────────────────────
const EXPIRES_KEY = 'jm_expires_at'

// Pre-QA-WEB-003 localStorage keys. Only ever READ once (migration) then
// deleted — never written again.
const LEGACY_REFRESH_KEY = 'jm_refresh_token'
const LEGACY_TOKEN_KEY = 'jm_token'

/** Headers/credentials every /api/mvp-auth login|signup|refresh|logout call needs
 *  so the browser will accept and send the httpOnly refresh cookie. Mobile
 *  sends no such header, so the backend keeps handing it `refreshToken` in
 *  the JSON body on those same routes. */
function authCookieFetchInit(): { credentials: RequestCredentials; headers: Record<string, string> } {
  return { credentials: 'include', headers: { 'x-jm-client': 'web' } }
}

function isAuthCookiePath(path: string): boolean {
  return path === '/api/mvp-auth/login'
    || path === '/api/mvp-auth/signup'
    || path === '/api/mvp-auth/refresh'
    || path === '/api/mvp-auth/logout'
}

// Defense in depth (WEB-BUG 2026-09-26 — reset-password 401 redirect loop):
// a route a logged-out visitor can legitimately be on. The global 401 handlers
// below must never hard-redirect these to /login — a stray authenticated call
// firing here (e.g. a badly-gated hook mounted app-wide) should just fail
// quietly; the caller already tolerates that. Root cause is fixed at the
// source (gate the caller on `isAuthenticated`, see useCompany.ts), but this
// is the backstop for the next one we miss.
//
// Covers every route under web/src/app/(blank-layout-pages) and
// web/src/app/(public) as of 2026-09-26: login, forgot-password,
// reset-password (blank-layout, session-optional) + booking/[subscriberId],
// quote/[subscriberId], portal/login, review/[token], sign/[subscriberId]/[token],
// pay/[token] (public, no session at all). No /signup route exists yet — add
// it here the day one ships.
const PUBLIC_AUTH_PATH_PREFIXES = [
  '/login',
  '/register',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/booking/',
  '/quote/',
  '/portal/login',
  '/review/',
  '/sign/',
  '/pay/',
] as const

export function isPublicAuthPath(pathname: string): boolean {
  return PUBLIC_AUTH_PATH_PREFIXES.some(p => pathname === p || pathname.startsWith(p))
}

// Background calls the app makes on its own (timers, polling, cache warm-up).
// A 401 on one of these just means the session expired in an idle tab — the
// normal expiry path handles it — so it is not reported to Corp telemetry as
// a "New Issue" (owner request 2026-09-27). 500s are still reported.
const BACKGROUND_API_PREFIXES = [
  '/api/realtime-config',
  '/api/notifications/unread-count',
  '/api/entitlements',
  '/api/company',
  '/api/lookups/navigation',
  '/api/mvp-auth/subscription/status',
] as const

export function shouldReportApi401(path: string, pathname: string): boolean {
  if (isPublicAuthPath(pathname)) return false
  const bare = path.split('?')[0]

  return !BACKGROUND_API_PREFIXES.some(p => bare === p || bare.startsWith(`${p}/`))
}

// Perform the actual network refresh call. `body.refreshToken` is set ONLY
// during the one-time legacy-session migration (item 8) — the normal path
// sends `{}` and relies entirely on the httpOnly cookie.
// Error code of the most recent failed refresh (e.g. 'NO_REFRESH_TOKEN' when
// the browser sent no cookie at all). Lets AuthContext skip the race-retry
// backoff when a retry cannot possibly succeed.
let _lastRefreshErrorCode: string | null = null

export function getLastRefreshErrorCode(): string | null {
  return _lastRefreshErrorCode
}

async function doRefresh(body: { refreshToken?: string }): Promise<boolean> {
  try {
    _lastRefreshErrorCode = null
    const { credentials, headers } = authCookieFetchInit()

    const r = await fetch(`${BACKEND_URL}/api/mvp-auth/refresh`, {
      method: 'POST',
      credentials,
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
    })

    if (!r.ok) {
      try { _lastRefreshErrorCode = ((await r.json()) as { code?: string })?.code ?? String(r.status) } catch { _lastRefreshErrorCode = String(r.status) }
      
return false
    }

    const data = await r.json() as { token?: string; expiresAt?: string }

    if (!data?.token) return false
    setAccessToken(data.token)

    if (data.expiresAt) {
      localStorage.setItem(EXPIRES_KEY, data.expiresAt)

      // WR-N08: let sibling windows adopt this token instead of each having
      // to mint (and rotate) their own the next time they need one.
      broadcastToken(data.token, data.expiresAt)
    }

return true
  } catch {
    return false
  }
}

// ─── Cross-window token sharing (WR-N08) ───────────────────────────────────
// Every window (main tab + every popout) is its own JS realm with its OWN
// in-memory access token — see auth-token.ts. Each one independently mints
// its token from the shared httpOnly refresh cookie, and every mint ROTATES
// that cookie (backend/src/routes/auth.ts). Opening several popouts in a
// working session used to mean several independent rotations in quick
// succession; the server's 60s grace window + compare-and-swap only
// guarantees a PAIR of overlapping refreshes resolves cleanly (see that
// file's "refresh race" comments) — a third overlapping rotation can still
// 401 with no useful code, which is exactly what showed up as a mid-session
// `session_expired` redirect (WR-N08) despite the JWT itself having plenty
// of TTL left. Sharing an already-valid token across windows avoids the
// redundant rotation instead of merely surviving the race.
//
// BroadcastChannel is same-origin only by browser design — a message posted
// here can never reach a different origin, `window.opener`, or any other
// window/tab that isn't this exact origin. Only `expiresAt` (already a
// non-secret, already-shared-via-localStorage timestamp — see EXPIRES_KEY
// above) is ever written to localStorage; the token itself is never
// persisted anywhere, only relayed in-memory between live windows exactly
// like the existing in-memory-only contract for a single window.
let _authChannel: BroadcastChannel | null | undefined

function getAuthChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  if (_authChannel === undefined) _authChannel = new BroadcastChannel('jm_auth_sync')

  return _authChannel
}

// Minimum remaining validity a token must have before this window will hand
// it to a sibling that's asking — never share a token that itself is about
// to need its own refresh.
const MIN_SHARE_VALIDITY_MS = 60_000

/** True when `candidateExpiresAt` (from an incoming broadcast) should replace
 *  what this window currently holds: adopt if we have no token yet, no known
 *  expiry, or the candidate is a strictly later generation. Never adopt an
 *  equal-or-older expiry — that would be the loser of a rotation race, or a
 *  stale echo of what we already have, downgrading/duplicating instead of
 *  advancing. Pure function — exported for unit testing without DOM globals. */
export function shouldAdoptBroadcastToken(hasCurrentToken: boolean, currentExpiresAt: string | null, candidateExpiresAt: string): boolean {
  if (!hasCurrentToken || !currentExpiresAt) return true

  return new Date(candidateExpiresAt).getTime() > new Date(currentExpiresAt).getTime()
}

/** True when this window should answer a sibling's "does anyone have a
 *  healthy token?" request: only when it actually holds one AND that token
 *  won't itself need refreshing within `MIN_SHARE_VALIDITY_MS` — sharing a
 *  token that's about to expire would just hand the asker the same problem.
 *  Pure function — exported for unit testing without DOM globals. */
export function hasShareableToken(hasToken: boolean, expiresAt: string | null, now: number): boolean {
  if (!hasToken || !expiresAt) return false

  return new Date(expiresAt).getTime() - now > MIN_SHARE_VALIDITY_MS
}

if (typeof window !== 'undefined') {
  const ch = getAuthChannel()

  ch?.addEventListener('message', (e: MessageEvent) => {
    // Never adopt a broadcast token in a window that isn't itself in a
    // logged-in state — e.g. a background tab sitting on /login after this
    // browser's session ended. AuthContext's own `jm_user` storage-event
    // listener is what tears a window down on a real logout/account switch;
    // this only shares a token between windows that both already believe
    // they're the same signed-in session.
    if (!localStorage.getItem('jm_user')) return

    const data = e.data as { type?: string; token?: string; expiresAt?: string } | undefined

    if (data?.type === 'token' && data.token && data.expiresAt) {
      const mineExpiresAt = localStorage.getItem(EXPIRES_KEY)

      if (shouldAdoptBroadcastToken(!!getAccessToken(), mineExpiresAt, data.expiresAt)) {
        setAccessToken(data.token)
        localStorage.setItem(EXPIRES_KEY, data.expiresAt)
      }
    } else if (data?.type === 'request-token') {
      // A sibling window is booting (or self-healing a 401) and asking if
      // anyone already holds a healthy token, to avoid minting — and
      // rotating — its own.
      const token = getAccessToken()
      const expiresAt = localStorage.getItem(EXPIRES_KEY)

      if (hasShareableToken(!!token, expiresAt, Date.now())) {
        ch.postMessage({ type: 'token', token, expiresAt })
      }
    }
  })

  // WR-N08 hardening: a real logout/account-switch removes `jm_user` from
  // localStorage, which fires this native `storage` event in every OTHER
  // same-origin window. AuthContext.tsx has its own listener for the full
  // logout/redirect UX; this one lives independently here so the in-memory
  // token itself is cleared immediately in EVERY sibling window regardless
  // of that listener's timing — a cleared token can't be broadcast further,
  // and a window with no token never answers a sibling's `request-token`.
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === 'jm_user' && e.newValue === null) setAccessToken(null)
  })
}

/** Broadcast a newly-minted token to every other same-origin window (main +
 *  all popouts) so they can adopt it instead of independently rotating the
 *  refresh cookie themselves the next time they need a token. Fire-and-
 *  forget — a window with the channel closed or no listener just misses it
 *  and falls back to its own cookie-based refresh as before. */
function broadcastToken(token: string, expiresAt: string): void {
  getAuthChannel()?.postMessage({ type: 'token', token, expiresAt })
}

/** Ask any other already-booted window for a currently-valid token instead
 *  of minting (and rotating) a new one. Resolves true if a usable token
 *  arrived in time and was adopted; false means no sibling answered in time
 *  — the caller should fall back to the normal cookie-based refresh. */
function requestTokenFromSiblingWindow(): Promise<boolean> {
  const ch = getAuthChannel()

  if (!ch) return Promise.resolve(false)

  return new Promise(resolve => {
    let settled = false

    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; token?: string; expiresAt?: string } | undefined

      if (settled || data?.type !== 'token' || !data.token || !data.expiresAt) return
      settled = true
      ch.removeEventListener('message', onMessage)
      setAccessToken(data.token)
      localStorage.setItem(EXPIRES_KEY, data.expiresAt)
      resolve(true)
    }

    ch.addEventListener('message', onMessage)
    ch.postMessage({ type: 'request-token' })

    // A same-origin BroadcastChannel reply is effectively instant (sub-ms) —
    // this is only a ceiling for "nobody answered", not an expected wait.
    setTimeout(() => {
      if (settled) return
      settled = true
      ch.removeEventListener('message', onMessage)
      resolve(false)
    }, 150)
  })
}

// Single-flight refresh, shared by: the boot sequence (every fresh window),
// the 401 self-heal path below, and AuthContext's proactive-refresh timer /
// on-focus handler / mount-restore effect. Only one network round-trip is
// ever in flight per window no matter how many callers ask at once.
let _refreshPromise: Promise<boolean> | null = null

/** Mint a fresh access token from the httpOnly refresh cookie (or, once, by
 *  migrating a legacy localStorage refresh token — see item 8). Safe to call
 *  from anywhere; concurrent callers share the one in-flight request. */
export function refreshAccessToken(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false)

  if (!_refreshPromise) {
    _refreshPromise = (async () => {
      // One-time migration: a session created before this deploy has its
      // refresh token sitting in localStorage. Exchange it once through the
      // body-based refresh (still tagged x-jm-client: web, so the response
      // ALSO sets the new httpOnly cookie) and delete the legacy keys so
      // this branch never runs again in this browser.
      const legacy = localStorage.getItem(LEGACY_REFRESH_KEY)

      if (legacy) {
        const ok = await doRefresh({ refreshToken: legacy })

        localStorage.removeItem(LEGACY_REFRESH_KEY)
        localStorage.removeItem(LEGACY_TOKEN_KEY)
        if (ok) return true

        // Legacy token was bad/expired — fall through in case a valid
        // httpOnly cookie already exists from some other prior refresh.
      }

      
return doRefresh({})
    })()

    // Clear once settled so a later expiry/logout can refresh again.
    _refreshPromise.finally(() => { _refreshPromise = null })
  }

  
return _refreshPromise
}

// Every window (main tab, reload, or popout) starts with `getAccessToken() ===
// null` and must refresh via the cookie before its first API call. This flag
// just ensures `request()` only ever KICKS OFF that boot refresh once per
// window — later callers with no token (logged-out state) don't re-attempt
// it on every request.
let _bootAttempted = false

async function ensureBooted(): Promise<void> {
  if (_bootAttempted || typeof window === 'undefined') return
  _bootAttempted = true

  // Only probe the cookie when there's a cached profile (or a legacy token)
  // to restore. A visitor who has never logged in (or explicitly unchecked
  // "Remember me" — AuthContext's mount effect clears jm_user in that case
  // before this ever runs) has no session to resurrect; skip the round trip
  // so the login page doesn't fire a doomed refresh on every load, and so
  // "remember me" still means what it says even though the httpOnly cookie
  // itself is a persistent 7-day cookie, not a browser-session cookie.
  if (!localStorage.getItem('jm_user') && !localStorage.getItem(LEGACY_REFRESH_KEY)) return

  // WR-N08: before minting our own token (which rotates the shared refresh
  // cookie), give an already-booted sibling window a brief chance to hand us
  // a still-healthy one instead — skips a redundant rotation entirely on the
  // common case of opening a popout while the main window is already logged
  // in. Scoped to `window.opener` (i.e. this window IS a popout) so a plain
  // reload/fresh load of the main tab — which has no sibling to ask and is
  // by far the most common boot path — never pays this round-trip's latency.
  // Never applies to the legacy-migration path (that one-time exchange must
  // run for real so the stale localStorage refresh token gets cleared).
  if (window.opener && !localStorage.getItem(LEGACY_REFRESH_KEY)) {
    const shared = await requestTokenFromSiblingWindow()

    if (shared) return
  }

  await refreshAccessToken()
}

// Guard: prevent 401 redirect from firing multiple times in parallel
let _redirecting401 = false

// ── Dunning lockout (ruling 2026-10-12) ─────────────────────────────────────
// A tenant whose subscription payment failed past the grace period is SUSPENDED: the backend answers every
// authenticated call with `403 { code: "BILLING_SUSPENDED", error }`. Mid-session that must END the session and
// show the login page with the reason (the user can then ask for a payment link there). This runs only on a
// RESPONSE we actually received (never in a fetch catch, so an aborted/unreachable request can never log anyone
// out), and shares the 401 redirect guard so parallel queries redirect once. No token is ever persisted: the
// in-memory access token is dropped; the httpOnly refresh cookie is refused by the server (and cleared) too.
async function responseHasBillingSuspendedCode(res: Response): Promise<boolean> {
  if (res.status !== 403) return false

  try {
    const body = await res.clone().json() as { code?: string }

    return body?.code === 'BILLING_SUSPENDED'
  } catch {
    return false
  }
}

/** Returns true (after starting the redirect) when `res` is a BILLING_SUSPENDED refusal on an authenticated call. */
async function redirectIfBillingSuspended(res: Response, path: string): Promise<boolean> {
  if (typeof window === 'undefined' || _redirecting401 || res.status !== 403) return false

  // The login call itself shows the message inline (with the payment-link button); refresh failures are handled by AuthContext.
  if (path.includes('/login') || path.includes('/refresh')) return false
  if (window.location.pathname === '/login' || isPublicAuthPath(window.location.pathname)) return false
  if (!(await responseHasBillingSuspendedCode(res))) return false

  _redirecting401 = true
  console.warn(`[api] 403 BILLING_SUSPENDED on ${path} — redirecting to /login`)
  setAccessToken(null)
  localStorage.removeItem('jm_user')
  localStorage.removeItem(EXPIRES_KEY)
  window.location.href = '/login?reason=billing_suspended'

  return true
}

// Guard: dedupe the BILLING_BLOCKED toast — a read-only tenant can fire several blocked writes
// in quick succession (e.g. a grid autosave + a manual save), and showing one toast per request
// was spammy. One toast per 5s window is plenty to tell the user what happened.
let _lastBillingBlockedToastAt = 0
const BILLING_BLOCKED_TOAST_WINDOW_MS = 5000

let _lastAccountCancelledToastAt = 0

async function handleBillingBlocked(body: { code?: string; error?: string }): Promise<void> {
  // G-27: a write refused on a cancelled (read-only) account. Own deduped toast + entitlements refresh.
  if (body?.code === 'ACCOUNT_CANCELLED') {
    try {
      const { refreshEntitlements } = await import('@/hooks/useEntitlements')

      refreshEntitlements()
    } catch { /* never break the request over this */ }

    const t = Date.now()

    if (t - _lastAccountCancelledToastAt < BILLING_BLOCKED_TOAST_WINDOW_MS) return
    _lastAccountCancelledToastAt = t

    const { toast } = await import('react-toastify')

    toast.error('This account is cancelled and read-only.')

    return
  }

  if (body?.code !== 'BILLING_BLOCKED') return

  // The banner (BillingBlockedBanner.tsx) already shows the account is read-only — refresh its
  // underlying entitlements query so a tenant that just got unblocked (or just got blocked
  // mid-session) sees the banner state update immediately, not after the next 60s staleTime tick.
  try {
    const { refreshEntitlements } = await import('@/hooks/useEntitlements')

    refreshEntitlements()
  } catch { /* never break the request over this */ }

  const now = Date.now()

  if (now - _lastBillingBlockedToastAt < BILLING_BLOCKED_TOAST_WINDOW_MS) return
  _lastBillingBlockedToastAt = now

  const { toast } = await import('react-toastify')

  toast.error('Your account is read-only until billing is updated.')
}

// Turn an error response body into a readable message instead of dumping raw
// JSON (e.g. a multi-line escaped Zod validation error) into toasts/dialogs.
// Handles the shapes actually returned across the backend: `{ error: "..." }`,
// `{ message: "..." }`, `{ error: { issues: [...] } }` (Hono/Zod validation
// hook), and plain text.
function firstZodIssueMessage(issues: unknown): string | null {
  if (Array.isArray(issues) && issues.length > 0 && typeof (issues[0] as any)?.message === 'string') {
    return (issues[0] as any).message
  }

  
return null
}

function extractErrorMessage(raw: string, status: number): string {
  if (!raw) return `Request failed: ${status}`

  try {
    const body = JSON.parse(raw)

    if (typeof body?.message === 'string' && body.message) return body.message
    if (typeof body?.error === 'string' && body.error) return body.error

    // Zod validation errors show up in a couple of shapes depending on the
    // route: `{ error: { issues: [...] } }`, or Hono's default hook which
    // nests a JSON-stringified issues array inside `error.message`.
    const fromIssues = firstZodIssueMessage(body?.error?.issues)

    if (fromIssues) return fromIssues

    if (typeof body?.error?.message === 'string') {
      try {
        const nested = firstZodIssueMessage(JSON.parse(body.error.message))

        if (nested) return nested
      } catch { /* error.message wasn't a JSON array — ignore */ }
    }
  } catch { /* not JSON — fall through to raw text */ }

  
return raw
}

// A 401 normally means the access token expired (e.g. the tab was
// backgrounded past the ~1h TTL — browsers throttle the scheduled-refresh
// timer, and React Query refetch-on-focus can fire with the stale token).
// Instead of bouncing the user to login or showing empty data, we
// transparently refresh once (via `refreshAccessToken()` above — the same
// single-flight cookie-based refresh used at boot) and retry the request.

// Set when the page is about to unload (popout closing, navigation, tab close).
// Any fetch that throws AFTER this flag is set is the browser aborting the
// in-flight request — NOT a real network failure. Don't wipe tokens in that case.
let _pageHiding = false

if (typeof window !== 'undefined') {
  const onHide = () => { _pageHiding = true }

  window.addEventListener('beforeunload', onHide)
  window.addEventListener('pagehide', onHide)
}

type RequestOptions = {
  method?: string
  body?: unknown
  headers?: Record<string, string>

  /** Internal: set on the one retry after a transparent token refresh. */
  _retry?: boolean

  /** BACKGROUND write (not caused by a click): a 402 FEATURE_NOT_IN_PLAN must NOT open the upgrade
   *  dialog - that dialog is only for explicit user actions. The error is still thrown to the caller. */
  background?: boolean
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, _retry = false, background = false } = options

  // Boot: this window has no access token yet — mint one from the httpOnly
  // refresh cookie before firing this (or any other) request. Single-flight
  // and only ever kicked off once per window (see ensureBooted above).
  if (typeof window !== 'undefined' && !getAccessToken() && !_retry) {
    await ensureBooted()
  }

  const token = typeof window !== 'undefined' ? getAccessToken() : null
  const isFormData = body instanceof FormData
  const cookiePath = isAuthCookiePath(path)

  let res: Response

  try {
    res = await fetch(`${BACKEND_URL}${path}`, {
    method,
    ...(cookiePath ? { credentials: 'include' as RequestCredentials } : {}),
    headers: {
      // Don't set Content-Type for FormData — browser sets it with the boundary automatically
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(cookiePath ? { 'x-jm-client': 'web' } : {}),
      ...headers
    },
    ...(body ? { body: isFormData ? body : JSON.stringify(body) } : {})
    })
  } catch (err: any) {
    // Distinguish "true network failure" from "browser aborted my fetch because
    // the page is closing/navigating". Chrome throws `TypeError: Failed to fetch`
    // (not AbortError) when window.close() cancels an in-flight fetch, and
    // document.visibilityState is often still 'visible' at that moment. The
    // reliable signal is the `_pageHiding` flag set by beforeunload/pagehide
    // handlers, which fire BEFORE the fetches reject. Treating an aborted
    // popout-closing fetch as "backend unavailable" was wiping the user's
    // session on every popout save — including from main-window code that
    // hadn't actually navigated.
    const aborted = err?.name === 'AbortError'
    const unloading = typeof document !== 'undefined' && document.visibilityState === 'hidden'

    if (_pageHiding || aborted || unloading) {
      throw new Error('Request aborted (page closing)')
    }


    // True network-level failure — backend is unreachable (tunnel down, PC off,
    // Railway redeploy/cold-start, transient wifi blip, etc.).
    //
    // ⚠️ Do NOT clear tokens or bounce to /login here. A backend being briefly
    // unreachable is NOT an auth failure — wiping the session logged EVERY open
    // client out on any backend restart/redeploy or momentary network hiccup
    // (the "users get logged out for no reason" bug). Instead route to
    // /maintenance, which keeps the session intact and auto-polls /api/auth/me
    // every 30s, returning the still-authenticated user to /dashboard the moment
    // the backend comes back. Real auth expiry is still handled by the 401 path
    // below (which is the ONLY place that clears the session).
    if (
      typeof window !== 'undefined' &&
      !window.location.pathname.startsWith('/maintenance') &&
      !window.location.pathname.startsWith('/login') &&
      !_redirecting401
    ) {
      _redirecting401 = true
      window.location.href = '/maintenance?reason=backend_unavailable'
    }

    throw new Error('Backend unavailable')
  }

  // ── Global 503 TENANT_PAUSED handler ──────────────────────────────────────
  // Backend signals tenant DB is in backup / upgrade / incident / maintenance.
  // Route to a dedicated /maintenance page with the canned message instead of
  // letting the app crash or spin on every subsequent request.
  if (res.status === 503 && typeof window !== 'undefined') {
    try {
      const body = await res.clone().json() as { code?: string; message?: string; reason?: string; until?: string | null }

      if (body?.code === 'TENANT_PAUSED' && !window.location.pathname.startsWith('/maintenance')) {
        const params = new URLSearchParams()

        if (body.message) params.set('msg', body.message)
        if (body.reason)  params.set('reason', body.reason)
        if (body.until)   params.set('until',  body.until)
        window.location.href = `/maintenance?${params.toString()}`
        throw new Error('Tenant paused — redirecting to /maintenance')
      }
    } catch { /* not JSON or already on maintenance — fall through */ }
  }

  // ── Global accounting-reauth detector (QBO / Xero / … via UFL) ────────────
  // Every UFL-backed endpoint that hits an expired provider token answers one
  // of two shapes only (never 401, never 5xx — see docs/QBO-Reconnect-Prompt-Plan.md):
  //   409 { code: "ACCOUNTING_AUTH_EXPIRED" }        — uflError() in ufl-sync.ts
  //   200 { status: "REAUTH_REQUIRED" }               — qbo.ts status/test/refresh routes
  // Detect both and broadcast a window event so any banner mounted in the app
  // (AccountingReauthBanner) can react — this does NOT change how the
  // response propagates to the caller: the 409 still falls through to the
  // `!res.ok` throw below, and the 200 body still resolves normally via
  // res.json(). Callers keep their existing error handling untouched.
  // Tracks the ACCOUNTING_AUTH_EXPIRED code across into the `!res.ok` throw
  // below (as a non-message property on the Error) so callers that WANT to
  // branch on it can — e.g. GlAccountPicker/AccountingSettingsPanel showing
  // "Reconnect QuickBooks to load accounts" instead of a generic error.
  // The thrown Error's type/message/propagation path is unchanged either way.
  let _acctAuthExpiredCode: string | undefined

  if (typeof window !== 'undefined') {
    try {
      const body = await res.clone().json() as {
        code?: string
        status?: string
        message?: string
        error?: string
        provider?: string
        providerName?: string
      }

      const isAcctAuthExpired = res.status === 409 && body?.code === 'ACCOUNTING_AUTH_EXPIRED'
      const isReauthRequired = body?.status === 'REAUTH_REQUIRED'

      if (isAcctAuthExpired) _acctAuthExpiredCode = body.code

      if (isAcctAuthExpired || isReauthRequired) {
        // 409 bodies (ufl-sync.ts uflError()) carry the human message in
        // `error`; 200 REAUTH_REQUIRED bodies (qbo.ts) carry it in `message`.
        window.dispatchEvent(new CustomEvent('jm:accounting-reauth', {
          detail: { provider: body.provider, providerName: body.providerName, message: body.message ?? body.error }
        }))
      }
    } catch { /* not JSON, empty body, etc. — ignore and fall through */ }
  }

  // ── Global 402 FEATURE_NOT_IN_PLAN handler (tier gating, Phase 2 enforcement) ─
  // A feature-guarded write answers `402 { code: "FEATURE_NOT_IN_PLAN", feature,
  // minimumPlan }` (backend/src/lib/feature-guard.ts). Broadcast a window event
  // so UpgradeDialogProvider (mounted app-wide in Providers.tsx) opens the same
  // upgrade dialog a locked nav item or UpgradeGate would show — a surface we
  // forgot to wrap still ends in an upgrade prompt instead of a raw error.
  // The thrown Error below carries `code: "FEATURE_NOT_IN_PLAN"` and a message
  // that already explains the situation, so a caller's generic
  // `catch (err) { toast.error(err.message) }` shows the same story the
  // dialog does rather than a raw/ugly server error — callers that want to
  // suppress their own toast entirely can check `err.code` first.
  //
  // IMPORTANT: only auto-open the dialog for WRITES (POST/PUT/PATCH/DELETE).
  // A background GET (e.g. a read-only panel that forgot to gate its own
  // `enabled`) must degrade quietly — popping the upgrade dialog on every
  // silent background poll of a locked feature was surfacing as a jarring
  // "surprise dialog" on baseline screens (e.g. a history panel embedded on
  // an otherwise-unlocked Client/Job/Quote/Invoice/Request detail). The typed
  // `code`/message are still attached either way so callers can react.
  let _featureNotInPlanCode: string | undefined
  let _featureNotInPlanMessage: string | undefined

  if (typeof window !== 'undefined' && res.status === 402) {
    try {
      const body = await res.clone().json() as { code?: string; feature?: string; minimumPlan?: string }

      if (body?.code === 'FEATURE_NOT_IN_PLAN' && body.feature) {
        _featureNotInPlanCode = body.code
        _featureNotInPlanMessage = body.minimumPlan
          ? `This feature requires the ${body.minimumPlan} plan or higher.`
          : 'This feature isn’t included in your current plan.'
        const isWrite = method !== 'GET' && method !== 'HEAD'

        if (isWrite && !background) {
          window.dispatchEvent(new CustomEvent('jm:feature-not-in-plan', {
            detail: { feature: body.feature, minimumPlan: body.minimumPlan }
          }))
        }
      }
    } catch { /* not JSON — ignore and fall through */ }
  }

  // ── Global 402 BILLING_BLOCKED handler (Gap 1, docs/plans/CORP_RECURRING_BILLING_PLAN.md
  // §7 ruling D5) ─────────────────────────────────────────────────────────────
  // A write refused by `backend/src/middleware/billing-block.ts` answers
  // `402 { code: "BILLING_BLOCKED", error }`. The persistent top-of-app banner
  // (BillingBlockedBanner.tsx) already shows the account is read-only — this is
  // just the per-action feedback for the write that was actually refused, a
  // clear toast rather than a raw/ugly server error or a crash/redirect. Only
  // fires for a genuine write attempt (never a background GET, which this
  // code can't even be returned for — billing-block.ts always allows GETs).
  if (typeof window !== 'undefined' && res.status === 402 && !background) {
    try {
      const body = await res.clone().json() as { code?: string; error?: string }

      await handleBillingBlocked(body)
    } catch { /* not JSON — ignore and fall through */ }
  }

  // ── Global 403 BILLING_SUSPENDED handler (dunning lockout, ruling 2026-10-12) ─────────
  if (await redirectIfBillingSuspended(res, path)) throw new Error('Account suspended — redirecting to login')

  // ── Global 503 CHAT_UNAVAILABLE handler (Rocket.Chat outage) ──────────────
  // Backend classifies an RC-side outage (license/user limit, RC unreachable,
  // RC not configured) as 503 { code: "CHAT_UNAVAILABLE", reason }. This is
  // never a JobMaster bug — tag the thrown error below so ChatWindow /
  // initRocketChat can show a quiet, friendly state instead of a raw error,
  // and skip Corp telemetry reporting (the backend already logs
  // `[chat-unavailable]` server-side; reporting it as an api_error would just
  // page on an RC outage nobody here can fix from this app).
  let _chatUnavailableReason: string | undefined
  if (res.status === 503) {
    try {
      const body = await res.clone().json() as { code?: string; reason?: string }
      if (body?.code === 'CHAT_UNAVAILABLE') _chatUnavailableReason = body.reason
    } catch { /* not JSON — ignore and fall through */ }
  }

  // ── Global 401 handler ────────────────────────────────────────────────────
  // If the backend returns 401, the session was kicked or the token is invalid.
  // Clear localStorage and redirect to login (unless we're already on the login page).
  // Guard: only redirect once — prevents infinite loop when multiple queries 401 simultaneously
  if (res.status === 401 && typeof window !== 'undefined' && !_redirecting401) {
    const isLoginCall = path.includes('/login') || path.includes('/refresh')
    const alreadyOnLogin = window.location.pathname === '/login'

    // Defense in depth (WEB-BUG 2026-09-26): a logged-out visitor on a public/
    // auth page (login, reset-password, a public pay/sign/booking link, etc.)
    // must never be hard-redirected by a stray authenticated call — the page
    // itself has no session to lose, and the caller already tolerates the
    // failure. See isPublicAuthPath() above.
    const onPublicPath = isPublicAuthPath(window.location.pathname)

    if (!isLoginCall && !alreadyOnLogin && !onPublicPath) {
      // WEB-042 (2026-09-17 QA): the backend now actually enforces session
      // revocation server-side (see backend/src/index.ts JWT middleware +
      // backend/src/lib/session-cache.ts) and tags a revoked-session 401 with
      // `code: "SESSION_REVOKED"`. A revoked session's refresh token is gone
      // too — the /refresh call below fails on its own — so this still just
      // falls through to the same redirect, but reading the code lets the
      // login page show an accurate reason instead of always guessing "kicked".
      //
      // Single-session mode (Rick, 2026-09-24): a session revoked because the
      // account signed in elsewhere gets its own code, SIGNED_IN_ELSEWHERE —
      // never worth a refresh attempt (the server already knows this exact
      // session is dead and will 401 the refresh too with the same code), so
      // skip straight to the redirect with the specific message.
      let revoked401Code: string | undefined

      try {
        const body = await res.clone().json() as { code?: string }

        revoked401Code = body?.code
      } catch { /* not JSON — ignore */ }

      let isSignedInElsewhere = revoked401Code === 'SIGNED_IN_ELSEWHERE'

      // First, try a transparent one-time refresh + retry. An expired access
      // token (backgrounded tab past TTL) self-heals here without bouncing the
      // user or showing empty data. `_retry` guards against an infinite loop —
      // if the retried request ALSO 401s, the session is genuinely over.
      if (!_retry && !isSignedInElsewhere) {
        const refreshed = await refreshAccessToken()

        if (refreshed) return request<T>(path, { ...options, _retry: true })

        // The refresh call itself may be the one that 401'd with
        // SIGNED_IN_ELSEWHERE (the original request's own 401 could have been
        // an ordinary expired-access-token 401 with no useful code at all).
        if (getLastRefreshErrorCode() === 'SIGNED_IN_ELSEWHERE') isSignedInElsewhere = true
      }

      _redirecting401 = true
      console.warn(`[api] 401 on ${path} — ${isSignedInElsewhere ? 'signed in elsewhere' : 'refresh failed'}, redirecting to /login`)
      setAccessToken(null)
      localStorage.removeItem('jm_user')
      localStorage.removeItem(EXPIRES_KEY)
      const reason = isSignedInElsewhere ? 'signed_in_elsewhere' : revoked401Code === 'SESSION_REVOKED' ? 'session_kicked' : 'session_expired'

      window.location.href = `/login?reason=${reason}`
      throw new Error('Session expired — redirecting to login')
    }
  }

  if (!res.ok) {
    const error = await res.text()

    // Silent error reporting to Corp DB — 500+ server errors and 401 auth errors
    // only, EXCLUDING a classified CHAT_UNAVAILABLE 503 (RC outage, not a
    // JobMaster bug — see the handler above).
    if ((res.status >= 500 && !_chatUnavailableReason) || (res.status === 401 && shouldReportApi401(path, typeof window !== 'undefined' ? window.location.pathname : ''))) {
      try { reportApiError(path, res.status, error) } catch { /* never break the app */ }
    }

    const err = new Error(
      _chatUnavailableReason ? 'Chat is temporarily unavailable. Please try again later.' : (_featureNotInPlanMessage ?? extractErrorMessage(error, res.status))
    ) as Error & { code?: string; reason?: string; isChatUnavailable?: boolean }

    if (_acctAuthExpiredCode) err.code = _acctAuthExpiredCode
    if (_featureNotInPlanCode) err.code = _featureNotInPlanCode
    if (res.status === 403 && /"code"\s*:\s*"BILLING_SUSPENDED"/.test(error)) err.code = 'BILLING_SUSPENDED'

    if (_chatUnavailableReason) {
      err.code = 'CHAT_UNAVAILABLE'
      err.reason = _chatUnavailableReason
      err.isChatUnavailable = true
    }
    throw err
  }

  // Handle 204 No Content
  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body }),
  /** POST made by the app itself, not by a user click (e.g. the automatic chat login): never opens the upgrade dialog. */
  postBackground: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body, background: true }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' })
}

/**
 * Build safe HTTP headers with optional Bearer auth.
 *
 * NEVER returns `Authorization: Bearer null` — when the token is missing,
 * the header is OMITTED entirely. That matters because the backend treats
 * "Bearer null" as a presented-but-invalid token (auth path) and may then
 * stamp downstream INSERTs with the SENTINEL tenant_id, creating orphan
 * rows that don't belong to any real tenant.
 *
 * Use this in any code path that builds headers for `fetch()` directly
 * (file downloads via res.blob(), multipart uploads, etc.). For JSON CRUD,
 * prefer `api.get/post/patch/delete` which already does this internally
 * AND handles 401 / 503 globally.
 *
 * @param extra Additional headers to merge on top
 * @returns Headers object — `Content-Type: application/json` by default,
 *          plus `Authorization: Bearer <token>` IF and only if token exists.
 */
export function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = { 'Content-Type': 'application/json', ...(extra ?? {}) }
  const token = typeof window !== 'undefined' ? getAccessToken() : null

  if (token) out.Authorization = `Bearer ${token}`
  
return out
}

/**
 * `fetch()` wrapper that uses `authHeaders()` for auth and routes 401/503
 * through the same global handlers as `api.*`. Use this when you need the
 * raw `Response` object (e.g. for `.blob()`, `.text()`, streaming).
 *
 * Unlike `api.*`, this does NOT parse the JSON body — the caller controls
 * what to do with the response. But it DOES throw / redirect on 401 like
 * the other helpers, so you never silently fall back to unauthenticated.
 */
export async function safeFetch(
  path: string,
  init?: RequestInit & { extraHeaders?: Record<string, string> },

  /** Internal: set on the one retry after a transparent token refresh — mirrors request()'s _retry. */
  _retry = false
): Promise<Response> {
  // Same boot rule as request() — a fresh window has no in-memory token yet.
  if (typeof window !== 'undefined' && !getAccessToken() && !_retry) {
    await ensureBooted()
  }

  const { extraHeaders, headers, ...rest } = init ?? {}
  const merged = { ...authHeaders(extraHeaders), ...(headers as Record<string, string> | undefined) }
  const url = path.startsWith('http') ? path : `${BACKEND_URL}${path}`
  const res = await fetch(url, { ...rest, headers: merged })

  if (res.status === 401 && typeof window !== 'undefined' && !_redirecting401) {
    const isAuthCall = path.includes('/login') || path.includes('/refresh')
    const alreadyOnLogin = window.location.pathname === '/login'

    // Defense in depth (WEB-BUG 2026-09-26) — see the matching comment + isPublicAuthPath() above.
    const onPublicPath = isPublicAuthPath(window.location.pathname)

    if (!isAuthCall && !alreadyOnLogin && !onPublicPath) {
      // WEB-042: see the matching comment in request()'s 401 handler above.
      let revoked401Code: string | undefined

      try {
        const body = await res.clone().json() as { code?: string }

        revoked401Code = body?.code
      } catch { /* not JSON — ignore */ }

      let isSignedInElsewhere = revoked401Code === 'SIGNED_IN_ELSEWHERE'

      // WR-N08: safeFetch used to redirect to /login on ANY 401, never even
      // trying the same transparent refresh+retry request() gets — a plain
      // expired-access-token 401 (the common, self-healable case: a
      // background tab's token lapsed past its TTL) was being treated
      // exactly like a genuinely revoked/dead session. Give it the same
      // one-shot self-heal before giving up.
      if (!_retry && !isSignedInElsewhere) {
        const refreshed = await refreshAccessToken()

        if (refreshed) return safeFetch(path, init, true)

        if (getLastRefreshErrorCode() === 'SIGNED_IN_ELSEWHERE') isSignedInElsewhere = true
      }

      _redirecting401 = true
      console.warn(`[safeFetch] 401 on ${path} — ${isSignedInElsewhere ? 'signed in elsewhere' : 'refresh failed'}, redirecting to /login`)
      setAccessToken(null)
      localStorage.removeItem('jm_user')
      localStorage.removeItem(EXPIRES_KEY)
      const reason = isSignedInElsewhere ? 'signed_in_elsewhere' : revoked401Code === 'SESSION_REVOKED' ? 'session_kicked' : 'session_expired'

      window.location.href = `/login?reason=${reason}`
      throw new Error('Session expired — redirecting to login')
    }
  }

  // Dunning lockout — same handling as request() above.
  if (await redirectIfBillingSuspended(res, path)) throw new Error('Account suspended — redirecting to login')

  // Same 402 FEATURE_NOT_IN_PLAN handling as request() above — a caller that
  // reaches for safeFetch() for a write (e.g. a raw multipart POST) must still
  // end in the shared upgrade dialog instead of a raw error. GETs stay quiet.
  if (typeof window !== 'undefined' && res.status === 402) {
    try {
      const body = await res.clone().json() as { code?: string; feature?: string; minimumPlan?: string }
      const method = (init?.method ?? 'GET').toUpperCase()
      const isWrite = method !== 'GET' && method !== 'HEAD'

      if (isWrite && body?.code === 'FEATURE_NOT_IN_PLAN' && body.feature) {
        window.dispatchEvent(new CustomEvent('jm:feature-not-in-plan', {
          detail: { feature: body.feature, minimumPlan: body.minimumPlan }
        }))
      }

      // Same 402 BILLING_BLOCKED handling as request() above — see that comment block.
      const blockedBody = body as { code?: string; error?: string }

      if (isWrite) await handleBillingBlocked(blockedBody)
    } catch { /* not JSON — ignore and fall through */ }
  }


return res
}
